import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFocusTrap } from '@prophecy/utils/react/focus';
import { useControlledState } from '@prophecy/utils/react/hooks';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Provider, Root, Portal, Trigger } from '@radix-ui/react-tooltip';
import { isNil } from 'lodash-es';
import React, { isValidElement } from 'react';
import { Stack } from '../Layout';
import { resolveCssVariable, theme } from '../theme';
import { StyledArrow, StyledContent, StyledCopyIcon, StyledDetail, StyledMarkDown, tokens } from './styled';
import { UseMarkdownFor } from './types';
export * from './types';
export function Tooltip({ title, detail, visible, onVisibleChange, children, overlayClassName, overlayStyle, placement: side = 'top', placementOffset: sideOffset = 8, align, hasCopy = false, alignOffset, delay = 100, disabled, isMarkdown = UseMarkdownFor.none, forceTooltipContentColor = true, showArrow = false, portalled = true, ...contentProps }) {
    const [_visible, toggleVisible] = useControlledState({
        value: !title && !detail || disabled ? false : visible,
        onChange: onVisibleChange
    });
    const titleRef = /*#__PURE__*/ React.createRef();
    const detailsRef = /*#__PURE__*/ React.createRef();
    const container = useFocusTrap(Boolean(_visible));
    const onCopy = async (e)=>{
        e.stopPropagation();
        const text = titleRef?.current?.innerText || '';
        const details = detailsRef?.current?.innerText || '';
        await navigator.clipboard.writeText(`${text}\n${details}`);
    };
    // If child has aria-label, set aria-describedby to undefined to prevent duplicate screen reading
    const hasAriaLabel = /*#__PURE__*/ isValidElement(children) && children.props?.['aria-label'];
    const triggerProps = hasAriaLabel ? {
        'aria-describedby': undefined
    } : {};
    const content = /*#__PURE__*/ _jsx(FocusScope, {
        asChild: true,
        trapped: false,
        onUnmountAutoFocus: (e)=>e.preventDefault(),
        onMountAutoFocus: (e)=>e.preventDefault(),
        children: /*#__PURE__*/ _jsxs(StyledContent, {
            className: overlayClassName,
            $forceTooltipContentColor: forceTooltipContentColor,
            style: overlayStyle,
            side: side,
            align: align,
            sideOffset: sideOffset,
            alignOffset: alignOffset,
            ref: container,
            tabIndex: -1,
            ...contentProps,
            "data-test-id": "tooltip-content",
            children: [
                /*#__PURE__*/ _jsxs(Stack, {
                    direction: "vertical",
                    children: [
                        title && /*#__PURE__*/ _jsxs(Stack, {
                            direction: "horizontal",
                            width: "100%",
                            align: "space-between",
                            gap: theme.spaces.x4,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    ref: titleRef,
                                    children: (isMarkdown === UseMarkdownFor.title || isMarkdown === UseMarkdownFor.both) && typeof title === 'string' ? /*#__PURE__*/ _jsx(StyledMarkDown, {
                                        children: title
                                    }) : title
                                }),
                                hasCopy && /*#__PURE__*/ _jsx(StyledCopyIcon, {
                                    size: "xs",
                                    type: "default",
                                    onClick: onCopy
                                })
                            ]
                        }),
                        detail && /*#__PURE__*/ _jsxs(Stack, {
                            direction: "horizontal",
                            width: "100%",
                            align: "left",
                            children: [
                                /*#__PURE__*/ _jsx(StyledDetail, {
                                    ref: detailsRef,
                                    style: {
                                        paddingTop: 'none'
                                    },
                                    children: (isMarkdown === UseMarkdownFor.detail || isMarkdown === UseMarkdownFor.both) && typeof detail === 'string' ? /*#__PURE__*/ _jsx(StyledMarkDown, {
                                        children: detail
                                    }) : detail
                                }),
                                hasCopy && !title && /*#__PURE__*/ _jsx(StyledCopyIcon, {
                                    size: "xs",
                                    type: "default",
                                    onClick: onCopy
                                })
                            ]
                        })
                    ]
                }),
                showArrow && /*#__PURE__*/ _jsx(StyledArrow, {
                    offset: parseInt(resolveCssVariable(tokens.Arrow.offset), 10)
                })
            ]
        })
    });
    return /*#__PURE__*/ _jsx(Provider, {
        children: /*#__PURE__*/ _jsxs(Root, {
            open: _visible,
            delayDuration: delay,
            onOpenChange: toggleVisible,
            children: [
                /*#__PURE__*/ _jsx(Trigger, {
                    asChild: true,
                    ...triggerProps,
                    children: children
                }),
                portalled ? /*#__PURE__*/ _jsx(Portal, {
                    children: content
                }) : content
            ]
        })
    });
}
export const renderWithTooltip = (content, tooltip)=>{
    if (isNil(tooltip)) return content;
    const tooltipProps = /*#__PURE__*/ isValidElement(tooltip) || typeof tooltip === 'string' ? {
        title: tooltip,
        placement: 'top',
        isMarkdown: 'title',
        overlayStyle: {
            maxWidth: '500px',
            maxHeight: '300px',
            overflow: 'auto',
            padding: theme.spaces.x12
        }
    } : tooltip;
    return /*#__PURE__*/ _jsx(Tooltip, {
        ...tooltipProps,
        children: content
    });
};
